import { Ayah } from '@app-types/KuranTypes'
import { type UserDataType } from '@app-types/UserDataType'
import LoadingSpinner from '@components/shared/Loading/Loading'
import PageAndSurahLine, { PageData } from '@components/shared/PageInfoRow/PageInfoRow'
import { useFocusModeContext } from '@contexts/FocusModeContext'
import { useUserData } from '@contexts/UserDataContext'
import classNames from 'classnames'
import { getAllAyahIds } from 'helpers/kuran'
import { hasNativeQuranBridge } from 'helpers/nativeQuranBridge'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo } from 'react'
import HayratKuranPage from '../HayratPage/HayratKuranPage'
import QuranLearningMode from '../QuranLearningMode/QuranLearningMode'
import QuranMealPage from '../QuranMealPage/QuranMealPage'
import MealliKuranPage from '../MealliKuranPage/MealliKuranPage'
import { getOneAyah } from './helpers'

const SoundPlayer = dynamic(() => import('./SoundPlayer'), { ssr: false })

const SECDE_STRING = '۩'

type KuranPageProps = {
  page: string
  ayahs: Ayah[]
  pageData: PageData
  mobileReader?: boolean
  showSoundPlayer?: boolean
}

export default function KuranPageComponent({
  page,
  ayahs,
  pageData,
  mobileReader = false,
  showSoundPlayer = true,
}: KuranPageProps) {
  const { userData } = useUserData()
  const router = useRouter()
  const isFocusMode = useFocusModeContext()

  useEffect(() => {
    if (
      mobileReader ||
      router.pathname === '/app-reader' ||
      (typeof navigator !== 'undefined' && !navigator.onLine) ||
      hasNativeQuranBridge()
    ) {
      return
    }

    // prefetch next page
    const nextPage = parseInt(page) + 1
    if (nextPage < 605) {
      setTimeout(() => {
        router.prefetch(`/kuran/${nextPage}`)
      }, 1000)
    }
  }, [mobileReader, page, router.pathname])

  const isPageWithSecdeAyah = useCallback(() => {
    return ayahs?.some((ayah) => {
      return ayah?.words?.some((word) => word?.arapca?.includes(SECDE_STRING))
    })
  }, [ayahs])

  const hideMeal = userData.kuranMealType === 'hide'
  const hideTooltip = userData.kuranTooltip === 'hide'

  // Memoize the sound files to prevent unnecessary re-renders
  const memoizedSoundFiles = useMemo(() => getAllAyahIds(ayahs), [ayahs])

  if (!ayahs?.length || !pageData) return <LoadingSpinner />

  return (
    <div
      onDoubleClick={() => {
        if (userData.kuranType !== 'only-meal') {
          document.dispatchEvent(new Event('playPauseEvent'))
        }
      }}
    >
      <PageAndSurahLine pageData={pageData} page={page} />
      {isPageWithSecdeAyah() && (
        <div className="bg-lime-100/80 mb-2 py-1 rounded-sm font-medium dark:text-gray-900 text-xs text-center">
          Dikkat: Bu sayfada secde ayeti bulunmaktadır.
        </div>
      )}
      {userData.kuranType === 'default' &&
        renderKuran({ userData, ayahs, hideMeal, hideTooltip })}
      {userData.kuranType === 'hayrat' && (
        <HayratKuranPage isPageWithSecdeAyah={isPageWithSecdeAyah()} />
      )}
      {userData.kuranType === 'learning' && <QuranLearningMode ayahs={ayahs} />}
      {userData.kuranType === 'only-meal' && <QuranMealPage ayahs={ayahs} />}
      {userData.kuranType === 'mealli' && <MealliKuranPage ayahs={ayahs} />}
      {showSoundPlayer && !isFocusMode && (
        <SoundPlayer page={page} soundFiles={memoizedSoundFiles} />
      )}
    </div>
  )
}

type RenderKuranArgs = {
  userData: UserDataType
  ayahs: any[]
  hideMeal: boolean
  hideTooltip: boolean
}

export function renderKuran({ userData, ayahs, hideMeal, hideTooltip }: RenderKuranArgs) {
  return (
    <div
      style={{
        direction: 'rtl',
      }}
      className={classNames(
        `flex select-none flex-wrap text-center dark:text-gray-300 md:justify-between`,
        userData.arabicFontFamily,
        {
          'justify-evenly': userData.arabicFontAlignment === 'middle',
        },
      )}
    >
      {ayahs.map((ayah, i) => {
        return getOneAyah({
          ayah,
          hideMeal,
          hideTooltip,
          ayahs,
          ayahNo: i,
          mealSource: userData.kuranMealSource,
        })
      })}
    </div>
  )
}
